import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, clearToken } from "@/lib/api";
import { initOneSignal, oneSignalLogin, oneSignalLogout } from "@/lib/onesignal";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setThemeState] = useState(localStorage.getItem("hi_theme") || "light");

  const applyTheme = useCallback((t) => {
    const root = document.documentElement;
    if (t === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("hi_theme", t);
  }, []);

  useEffect(() => { applyTheme(theme); }, [theme, applyTheme]);

  // Inizializza OneSignal una sola volta al caricamento dell'app.
  useEffect(() => { initOneSignal(); }, []);

  // Collega il dispositivo push all'identità dell'utente loggato (canale unico).
  useEffect(() => {
    if (user?.id) oneSignalLogin(user.id, { role: user.role, location_id: user.location_id });
  }, [user?.id, user?.role, user?.location_id]);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.theme) setThemeState(data.theme);
    } catch (e) {
      // 401 => sessione assente/scaduta: stato anonimo, non è un errore bloccante.
      // Puliamo il token locale eventualmente stantìo per evitare Bearer non validi.
      if (e?.response?.status === 401) clearToken();
      else console.error("Errore nel caricamento sessione:", e);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Se stiamo tornando dal callback OAuth (session_id nell'hash), NON eseguire
    // il controllo /auth/me: sarà AuthCallback a stabilire la sessione per primo.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    // Il token viene impostato dal backend come cookie httpOnly (non accessibile a JS)
    // e ANCHE salvato in locale come fallback resiliente (mobile/riapertura app).
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) setToken(data.token);
    setUser(data.user);
    if (data.user.theme) setThemeState(data.user.theme);
    return data.user;
  };

  const register = async (payload) => {
    // Registrazione cliente: il backend imposta il cookie httpOnly e restituisce l'utente.
    const { data } = await api.post("/portal/register", payload);
    if (data.token) setToken(data.token);
    setUser(data.user);
    if (data.user.theme) setThemeState(data.user.theme);
    return data.user;
  };

  const loginWithGoogleSession = async (sessionId) => {
    // Scambio del session_id (gestito da Emergent) -> cookie httpOnly `hi_token`.
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    if (data.token) setToken(data.token);
    setUser(data.user);
    if (data.user.theme) setThemeState(data.user.theme);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (e) { console.error("Errore durante il logout:", e); }
    clearToken();
    try { await oneSignalLogout(); } catch (e) { console.debug("OneSignal logout skip", e); }
    setUser(null);
    window.location.href = "/login";
  };

  const setTheme = async (t) => {
    setThemeState(t);
    if (user) {
      try { await api.put("/auth/theme", { theme: t }); }
      catch (e) { console.error("Impossibile salvare il tema:", e); }
    }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, loginWithGoogleSession, logout, theme, setTheme }}>
      {children}
    </AuthCtx.Provider>
  );
}
